import { z } from "zod";
/**
 * SCRIPT SUPERVISOR — the continuity check over learner-facing prose.
 *
 * Named for the role on a film set: the person who notices that the glass was
 * half-full in the wide shot and full in the close-up. They do not direct, do
 * not rewrite, and do not decide what happens in the scene. They say "that
 * contradicts what we already shot", and the scene is taken again.
 *
 * ITS AUTHORITY IS EXACTLY THAT, AND THIS SCHEMA IS WHERE THE LIMIT LIVES.
 * There is no field here through which it could add a fact, change a ruling,
 * name a new person, alter state, or re-rank anything — the output is a
 * boolean, a list of contradictions it observed, and a note for whoever writes
 * the replacement. CLAUDE.md Law 1 says deterministic code owns all truth;
 * a fifth model authority would be a violation however useful it looked, so
 * the shape of what it can say is bounded here rather than by instruction.
 *
 * `contradictions` are OBSERVATIONS about the prose, not corrections to it —
 * they exist so a rejection is auditable and so the debrief can show why a turn
 * was re-composed. They are never shown to the learner.
 */
const CONTRADICTION_MAX = 5;
const CONTRADICTION_NOTE_MAX = 300;
const SUPERVISOR_EDIT_FROM_MAX = 200;
const SUPERVISOR_EDIT_TO_MAX = 160;
const REPAIR_INSTRUCTION_MAX = 400;
/**
 * What KIND of thing a contradiction is, so that code — not the model — decides
 * which kinds are grounds for recomposing a scene.
 *
 * "presence" is the one that is never grounds. The engine's scene cast is a
 * floor (packages/engine/src/scene-cast.ts: deliberately conservative, "empty
 * means unknown"), so a person being in the room without being listed is not a
 * contradiction of anything the engine knows. Two days live and three
 * re-recordings showed the cheap model reports it anyway, however the prompt
 * is worded — "Hugh is present, but Hugh is not listed in the cast" — and each
 * such report threw away a clean turn. It is still recorded, because it is an
 * observation worth auditing; it is simply not actionable.
 */
export const ContradictionKindSchema = z.enum([
    /** Someone not in the cast is present, speaks, or acts. Recorded, never actionable. */
    "presence",
    /** A listed cast member is written as absent or unreachable in this scene. */
    "absence",
    /** An unlisted arrival, missing delivery/limitation, or description beyond the receipt. */
    "document",
    /** The same person does two incompatible things; an event that did not happen. */
    "event",
    /** Narration invents the player's thought, speech, consent, or undeclared action. */
    "agency",
    /** Praise or grading of the player's reasoning, prohibited in reactive narration. */
    "evaluation",
    /** The narrator asserts an unsupported private motive or psychological explanation. */
    "interpretation",
    /** A place that contradicts the recorded place. */
    "place",
    /** An established fact stated as its opposite. */
    "fact",
    /** A final ruling softened, reversed, or reopened. */
    "ruling",
]);
export const ContradictionSchema = z.strictObject({
    kind: ContradictionKindSchema,
    /** One short observation. Capped because a supervisor that returns a paragraph is writing. */
    note: z.string().min(1).max(CONTRADICTION_NOTE_MAX),
});
/** The kinds a rejection may act on. Everything but "presence". */
export const ACTIONABLE_CONTRADICTION_KINDS = new Set(["absence", "document", "event", "agency", "evaluation", "interpretation", "place", "fact", "ruling"]);
/**
 * A TARGETED SUBSTITUTION — the one way the supervisor may touch prose, and
 * it is a narrow one. "Wednesday" for "Thursday"; a wrong surname; a document
 * title spelled the way the client spells it. The engine applies an edit only
 * when `from` occurs exactly once in the prose and `to` is a handful of words,
 * and ships the result only if it passes every deterministic narrator-gate
 * check; anything else is discarded, never attempted. The supervisor still
 * cannot write a sentence, add a person, or invent a figure: `to` is bounded
 * here and validated downstream, by code.
 */
export const SUPERVISOR_EDIT_MAX = 3;
export const SUPERVISOR_EDIT_TO_MAX_WORDS = 12;
export const SupervisorEditSchema = z.strictObject({
    /** Exact text copied from the prose. Must occur exactly once, or the edit is ignored. */
    from: z.string().min(1).max(SUPERVISOR_EDIT_FROM_MAX),
    /** The replacement. Empty deletes the span. Capped in words by the engine. */
    to: z.string().max(SUPERVISOR_EDIT_TO_MAX),
});
export const ScriptSupervisorVerdictSchema = z.strictObject({
    /** True when the prose is consistent with the canon it was given. */
    consistent: z.boolean(),
    /**
     * What it saw, one short observation each, typed by kind. Capped because a
     * supervisor that returns a paragraph is writing, and writing is not its job.
     */
    contradictions: z.array(ContradictionSchema).max(CONTRADICTION_MAX),
    /**
     * Targeted substitutions that would resolve the contradictions, when a word
     * or short phrase is all that is wrong. Empty when the fix needs a rewrite.
     * Tried FIRST by the engine, before any model is asked to rewrite.
     */
    edits: z.array(SupervisorEditSchema).max(SUPERVISOR_EDIT_MAX),
    /**
     * One line of guidance for the replacement prose, or null. NOT the
     * replacement itself: it may say "this scene has only Marcus in it", never
     * "say this instead". A supervisor that supplies prose is a second narrator.
     * Fed to the NARRATOR's own repair call when no edit could be applied.
     */
    repair_instruction: z.string().min(1).max(REPAIR_INSTRUCTION_MAX).nullable(),
}).refine((verdict) => verdict.consistent === (verdict.contradictions.length === 0), {
    message: "Consistency must agree with the reported contradictions.",
    path: ["consistent"],
});
/**
 * THE VERDICT AS IT ARRIVES (Law 4; the stage-2 re-record, 2026-09-30). The caps above still
 * hold, but a verdict that broke one used to fail whole: retried once, the prose then shipped
 * unverified and the contradictions the supervisor had found were lost with it (Harborlight
 * turns 3, 6 and 9, one of them a line of Hugh's grading the learner's move). Code now bounds a
 * verdict before validating it:
 *
 *  - an edit past its caps is dropped, as the engine would never apply it ("anything else is
 *    discarded, never attempted"), and the first SUPERVISOR_EDIT_MAX edits are kept;
 *  - a note or repair instruction past its cap is cut at the last sentence that fits, or else at
 *    the last word, marked with an ellipsis;
 *  - the first CONTRADICTION_MAX contradictions are kept.
 *
 * Nothing is added or reworded, the consistency rule still holds, and anything else invalid stays
 * invalid. The provider's tool schema is the strict one: the pipe's output side.
 */
export function boundSupervisorVerdict(raw) {
    if (!isRecord(raw))
        return raw;
    const bounded = { ...raw };
    const { contradictions, edits, repair_instruction: repair } = raw;
    if (Array.isArray(contradictions)) {
        bounded["contradictions"] = contradictions.slice(0, CONTRADICTION_MAX).map((item) => (isRecord(item) && typeof item["note"] === "string" ? { ...item, note: clip(item["note"], CONTRADICTION_NOTE_MAX) } : item));
    }
    if (Array.isArray(edits)) {
        bounded["edits"] = edits.filter((item) => !(isRecord(item) && (tooLong(item["from"], SUPERVISOR_EDIT_FROM_MAX) || tooLong(item["to"], SUPERVISOR_EDIT_TO_MAX)))).slice(0, SUPERVISOR_EDIT_MAX);
    }
    if (typeof repair === "string")
        bounded["repair_instruction"] = clip(repair, REPAIR_INSTRUCTION_MAX);
    return bounded;
}
export const ScriptSupervisorVerdictIngressSchema = z.preprocess(boundSupervisorVerdict, ScriptSupervisorVerdictSchema);
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function tooLong(value, max) {
    return typeof value === "string" && value.length > max;
}
/** At most `max` characters: whole sentences when they fill at least half, else whole words and an ellipsis. */
function clip(text, max) {
    if (text.length <= max)
        return text;
    const room = text.slice(0, max - 1);
    const sentences = /^[\s\S]*[.!?]["”’']?(?=\s)/u.exec(room)?.[0];
    if (sentences !== undefined && sentences.length >= max / 2)
        return sentences;
    const words = room.replace(/\s+\S*$/u, "");
    return `${words.length > 0 ? words : room}…`;
}
//# sourceMappingURL=script-supervisor.js.map